'use client'

import { useEffect, useState } from 'react'

import Image from 'next/image'

import Autoplay from 'embla-carousel-autoplay'
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react'

import { SECTION_TITLE } from '@/lib/typography'

import { IMAGE_CARD_IMAGE, IMAGE_CARD_SCRIM, IMAGE_CARD_SHELL } from '@/components/blocks/image-card-effect'
import { MaybeHtml } from '@/components/blocks/maybe-html'
import { SECTION_PADDING, SITE_CONTAINER } from '@/components/layout/container'
import { Button } from '@/components/ui/button'
import { Carousel, CarouselContent, CarouselItem, type CarouselApi } from '@/components/ui/carousel'
import { cn } from '@/lib/utils'

export type GallerySlide = {

  /** Бүлгийн ЭХНИЙ зураг — слайдын бүтэн дэвсгэр */
  image?: string

  /** Слайдын гарчиг — «Гарчиг» талбар, хоосон бол бүлгийн нэр */
  title: string

  /** Тайлбар — rich text байж болно */
  body?: string
}

/**
 * Бүлэг бүрийг НЭГ ТОМ СЛАЙД болгосон галерей.
 *
 * ! УРЬД НЬ БҮЛЭГ БҮР ТУСДАА ХЭСЭГ, доторх 5 зураг нь эгнэж байв: дөрвөн
 * ! агуулах хуудсыг хэдэн дэлгэц уртасгаж, хооронд нь харьцуулах боломжгүй
 * ! байсан. Одоо бүлгийн ЭХНИЙ зураг л бүтэн слайд болж, гарчиг, тайлбар нь
 * ! зургийн дээр гарна — нүүр хуудасны үндсэн слайдертай ижил зарчим.
 */
export const GallerySlider = ({
  badge,
  slides,
  interval = 7000
}: {
  badge?: string
  slides: GallerySlide[]

  /** Автоматаар солигдох хугацаа (мс). 0 бол автомат солигдолтгүй. */
  interval?: number
}) => {
  const [api, setApi] = useState<CarouselApi>()
  const [current, setCurrent] = useState(0)

  /*
   * ! ХУЛГАНА ДЭЭР ЗОГСОНО. Слайд дээр гарчиг, тайлбар байгаа тул уншиж
   * ! байхад нь солигдвол бухимдуулна — `stopOnMouseEnter`. Гараар сум дарсан
   * ! ч дахин үргэлжилнэ (`stopOnInteraction: false`).
   */
  const [autoplay] = useState(() =>
    Autoplay({ delay: Math.max(interval, 1000), stopOnInteraction: false, stopOnMouseEnter: true })
  )

  useEffect(() => {
    if (!api) return

    const onSelect = () => setCurrent(api.selectedScrollSnap())

    api.on('select', onSelect)
    onSelect()

    return () => {
      api.off('select', onSelect)
    }
  }, [api])

  if (slides.length === 0) {
    return null
  }

  return (
    <section className={SECTION_PADDING}>
      <div className={cn(SITE_CONTAINER, 'space-y-8')}>
        <Carousel setApi={setApi} opts={{ loop: true, align: 'start' }} plugins={interval > 0 ? [autoplay] : []}>
          <CarouselContent className='ml-0'>
            {slides.map((slide, index) => (
              <CarouselItem key={slide.title || index} className='basis-full p-0'>
                <div className={cn(IMAGE_CARD_SHELL, 'h-100 rounded-3xl sm:h-125 lg:h-150')}>
                  {slide.image ? (
                    <Image
                      src={slide.image}
                      alt={slide.title}
                      fill
                      sizes='(min-width: 1280px) 1280px, 100vw'
                      priority={index === 0}
                      className={IMAGE_CARD_IMAGE}
                    />
                  ) : (
                    <div className='from-muted via-background to-muted absolute inset-0 bg-gradient-to-br' />
                  )}

                  {slide.image && <div className={IMAGE_CARD_SCRIM} />}

                  {/* Бичиг доод талд — зургийн гол хэсгийг хаахгүй */}
                  <div className='relative flex h-full flex-col justify-end gap-3 p-6 sm:p-10 lg:p-14'>
                    {badge && (
                      <span
                        className={cn(
                          'text-xl font-semibold tracking-widest uppercase',
                          slide.image ? 'text-white/80' : 'text-muted-foreground'
                        )}
                      >
                        {badge}
                      </span>
                    )}

                    <h2
                      className={cn(SECTION_TITLE, 'max-w-3xl', slide.image && 'text-white')}
                    >
                      {slide.title}
                    </h2>

                    {slide.body ? (
                      <MaybeHtml
                        text={slide.body}
                        className={cn('max-w-2xl text-xl', slide.image ? 'text-white/90' : 'text-muted-foreground')}
                      />
                    ) : null}
                  </div>
                </div>
              </CarouselItem>
            ))}
          </CarouselContent>
        </Carousel>

        {slides.length > 1 && (
          <div className='flex items-center justify-center gap-4'>
            <Button
              type='button'
              variant='outline'
              size='icon'
              aria-label='Өмнөх'
              onClick={() => api?.scrollPrev()}
              className='rounded-full'
            >
              <ChevronLeftIcon className='size-4' />
            </Button>

            {/* Цэгүүд нь нүүр хуудасны слайдертай ижил хэлбэртэй */}
            <div className='flex items-center gap-2'>
              {slides.map((slide, index) => (
                <button
                  key={slide.title || index}
                  type='button'
                  aria-label={slide.title}
                  aria-current={index === current}
                  onClick={() => api?.scrollTo(index)}
                  className={cn(
                    'h-1.5 rounded-full transition-all',
                    index === current ? 'bg-foreground w-8' : 'bg-foreground/30 w-4'
                  )}
                />
              ))}
            </div>

            <Button
              type='button'
              variant='outline'
              size='icon'
              aria-label='Дараах'
              onClick={() => api?.scrollNext()}
              className='rounded-full'
            >
              <ChevronRightIcon className='size-4' />
            </Button>
          </div>
        )}
      </div>
    </section>
  )
}
